import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, FileText, Pencil, Trash2, Loader2, Send, CheckCircle, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  draft: { label: "Luonnos", cls: "bg-zinc-700/40 text-zinc-200" },
  sent: { label: "Lähetetty", cls: "bg-blue-600/30 text-blue-200" },
  accepted: { label: "Hyväksytty", cls: "bg-emerald-600/30 text-emerald-200" },
  rejected: { label: "Hylätty", cls: "bg-red-600/30 text-red-200" },
  expired: { label: "Vanhentunut", cls: "bg-zinc-600/30 text-zinc-300" },
  converted: { label: "Muunnettu laskuksi", cls: "bg-amber-500/20 text-amber-200" },
};

function QuoteForm({ quote, settings, onSaved, onClose }) {
  const isEdit = !!quote;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    recipient_name: quote?.recipient_name || "",
    recipient_email: quote?.recipient_email || "",
    recipient_address: quote?.recipient_address || "",
    recipient_business_id: quote?.recipient_business_id || "",
    quote_date: quote?.quote_date || new Date().toISOString().split("T")[0],
    valid_until:
      quote?.valid_until ||
      (() => {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        return d.toISOString().split("T")[0];
      })(),
    items:
      quote?.items?.length
        ? quote.items
        : [{ description: "", quantity: 1, unit: "kpl", unit_price: 0, vat_percent: settings?.default_vat_percent || 25.5, total: 0 }],
    notes: quote?.notes || "",
    status: quote?.status || "draft",
  });

  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    if (["quantity", "unit_price", "vat_percent"].includes(field)) {
      const qty = field === "quantity" ? value : items[i].quantity;
      const price = field === "unit_price" ? value : items[i].unit_price;
      const vat = field === "vat_percent" ? value : items[i].vat_percent;
      items[i].total = (Number(qty) || 0) * (Number(price) || 0) * (1 + (Number(vat) || 0) / 100);
    }
    setForm((f) => ({ ...f, items }));
  };

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const vatTotal = form.items.reduce(
    (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * ((Number(it.vat_percent) || 0) / 100),
    0,
  );
  const total = subtotal + vatTotal;

  const save = async (status) => {
    if (!form.recipient_name) {
      toast.error("Asiakkaan nimi puuttuu");
      return;
    }
    setLoading(true);
    try {
      const data = { ...form, status: status || form.status };
      if (isEdit) await api.put(`/quotes/items/${quote.id}`, data);
      else await api.post("/quotes/items", data);
      toast.success(isEdit ? "Tarjous päivitetty" : "Tarjous luotu");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="quote-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Voimassa saakka</Label>
          <Input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Tarjouspäivä</Label>
          <Input
            type="date"
            value={form.quote_date}
            onChange={(e) => setForm((f) => ({ ...f, quote_date: e.target.value }))}
            className="mt-1"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Asiakkaan nimi *</Label>
          <Input
            value={form.recipient_name}
            onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))}
            className="mt-1"
            data-testid="quote-recipient"
          />
        </div>
        <div>
          <Label className="text-xs">Sähköposti</Label>
          <Input
            value={form.recipient_email}
            onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Y-tunnus</Label>
          <Input
            value={form.recipient_business_id}
            onChange={(e) => setForm((f) => ({ ...f, recipient_business_id: e.target.value }))}
            className="mt-1"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Rivit</Label>
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-xs gap-1"
            onClick={() =>
              setForm((f) => ({
                ...f,
                items: [...f.items, { description: "", quantity: 1, unit: "kpl", unit_price: 0, vat_percent: 25.5, total: 0 }],
              }))
            }
          >
            <Plus className="w-3 h-3" /> Lisää rivi
          </Button>
        </div>
        {form.items.map((item, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-end">
            <div className="col-span-5">
              <Input
                value={item.description}
                onChange={(e) => updateItem(i, "description", e.target.value)}
                placeholder="Kuvaus"
                className="h-8 text-xs"
              />
            </div>
            <div className="col-span-1">
              <Input
                type="number"
                value={item.quantity}
                onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 0)}
                className="h-8 text-xs"
              />
            </div>
            <div className="col-span-2">
              <Input
                type="number"
                step="0.01"
                value={item.unit_price}
                onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                placeholder="Hinta"
                className="h-8 text-xs"
              />
            </div>
            <div className="col-span-2">
              <Input value={(Number(item.total) || 0).toFixed(2) + " €"} readOnly className="h-8 text-xs bg-muted" />
            </div>
            <div className="col-span-2 flex justify-end">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-destructive"
                onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        <div className="text-right text-sm space-y-0.5">
          <p className="text-muted-foreground">Veroton: {subtotal.toFixed(2)} €</p>
          <p className="font-bold">Yhteensä ALV:lla: {total.toFixed(2)} €</p>
        </div>
      </div>

      <div>
        <Label className="text-xs">Lisätiedot / ehdot</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          className="mt-1 text-sm"
          rows={2}
        />
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button variant="secondary" onClick={() => save("draft")} disabled={loading} className="gap-1">
          <Copy className="w-3 h-3" /> Luonnos
        </Button>
        <Button
          onClick={() => save("sent")}
          disabled={loading}
          className="gap-1 bg-amber-500 hover:bg-amber-400 text-zinc-900"
          data-testid="quote-save"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Lähetä tarjous
        </Button>
      </div>
    </div>
  );
}

export default function QuotesPanel({ settings, onConvertToInvoice }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotes/items");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko tarjous?")) return;
    try {
      await api.delete(`/quotes/items/${id}`);
      toast.success("Tarjous poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/quotes/items/${id}`, { status }, { params: {} });
      toast.success("Päivitetty");
      load();
    } catch (e) {
      // fallback: send a full PUT; but if PUT requires recipient_name, use patch via update endpoint not present – we just refetch on success
      toast.error(formatApiError(e));
    }
  };

  const convertToInvoice = async (q) => {
    try {
      const r = await api.post(`/quotes/${q.id}/convert-to-invoice`);
      toast.success(`Tarjous muunnettu laskuksi #${r.data.invoice_number}`);
      onConvertToInvoice && onConvertToInvoice(r.data);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="quotes-panel">
      <div className="flex justify-end">
        <Button
          size="sm"
          className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"
          onClick={() => {
            setEditItem(null);
            setShowForm(true);
          }}
          data-testid="quote-new"
        >
          <Plus className="w-4 h-4" /> Uusi tarjous
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Ei tarjouksia. Luo ensimmäinen asiakkaallesi!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((q) => {
            const st = STATUS_MAP[q.status] || STATUS_MAP.draft;
            return (
              <Card key={q.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold">#{q.quote_number}</span>
                        <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                      </div>
                      <p className="font-medium text-sm">{q.recipient_name}</p>
                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                        <span>Päivä: {q.quote_date}</span>
                        <span>Voimassa: {q.valid_until}</span>
                        <span className="font-semibold text-foreground">
                          {(Number(q.total_amount) || 0).toFixed(2)} €
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                      {q.status !== "accepted" && q.status !== "converted" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => setStatus(q.id, "accepted")}
                        >
                          <CheckCircle className="w-3 h-3 text-emerald-400" /> Hyväksytty
                        </Button>
                      )}
                      {q.status === "accepted" && !q.converted_to_invoice_id && (
                        <Button
                          size="sm"
                          className="h-7 text-xs gap-1 bg-amber-500 text-zinc-900 hover:bg-amber-400"
                          onClick={() => convertToInvoice(q)}
                          data-testid={`quote-convert-${q.id}`}
                        >
                          <ArrowRight className="w-3 h-3" /> Muunna laskuksi
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditItem(q);
                          setShowForm(true);
                        }}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive/60 hover:text-destructive"
                        onClick={() => remove(q.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editItem ? "Muokkaa tarjousta" : "Uusi tarjous"}</DialogTitle>
          </DialogHeader>
          <QuoteForm
            quote={editItem}
            settings={settings}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
